import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ProfileUpdateRequirementsData } from "@/service/_apiService"
import {
  usePatchProfileUpdate,
  useProfileUpdateRequirements,
} from "@/hooks/User/useProfileUpdate"
import { useGetOrgDepartmentsActive } from "@/hooks/useDeparments"
import moment, { Moment } from "moment"

// Campos soportados dinámicamente
const SUPPORTED_FIELDS = [
  "city",
  "phone",
  "department_id",
  "name",
  "lastname",
  "title",
  "address",
  "state",
  "location",
  "about_me",
  "linkedin",
  "twitter",
  "date_of_birth",
  "language",
  "country_id",
] as const

type SupportedField = (typeof SUPPORTED_FIELDS)[number]

const FIELD_LABELS: Record<SupportedField, string> = {
  city: "Ciudad",
  phone: "Teléfono",
  department_id: "Departamento / Área",
  name: "Nombre",
  lastname: "Apellido",
  title: "Cargo",
  address: "Dirección",
  state: "Estado / Provincia",
  location: "Ubicación",
  about_me: "Sobre mí",
  linkedin: "LinkedIn",
  twitter: "Twitter",
  date_of_birth: "Fecha de nacimiento",
  language: "Idioma",
  country_id: "País",
}

interface ProfileUpdateModalProps {
  open: boolean
  onClose: () => void
  requirements: ProfileUpdateRequirementsData
}

const ProfileUpdateModal = ({
  open,
  onClose,
  requirements,
}: ProfileUpdateModalProps) => {
  const { t } = useTranslation()
  const { mutateAsync: patchProfile, isLoading: isSaving } =
    usePatchProfileUpdate()
  const { data: departments = [] } = useGetOrgDepartmentsActive()

  // Solo renderizar los campos que el backend solicita y que el front soporta
  const activeFields = (requirements.fields ?? []).filter((f) =>
    SUPPORTED_FIELDS.includes(f as SupportedField)
  ) as SupportedField[]

  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [dateValue, setDateValue] = useState<Moment | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFieldChange = (field: SupportedField, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setError(null)

    // Validar que todos los campos requeridos tienen valor
    const missing = activeFields.filter((field) => {
      if (field === "date_of_birth") return !dateValue
      return !formValues[field] && formValues[field] !== 0
    })

    if (missing.length > 0) {
      const labels = missing.map((f) => FIELD_LABELS[f] ?? f).join(", ")
      setError(`Por favor completa los siguientes campos: ${labels}`)
      return
    }

    const payload: Record<string, any> = { ...formValues }

    if (activeFields.includes("date_of_birth") && dateValue) {
      payload.date_of_birth = dateValue.toISOString()
    }

    try {
      await patchProfile(payload)
      onClose()
    } catch {
      setError("Ocurrió un error al guardar. Intenta de nuevo.")
    }
  }

  const handleRemindLater = async () => {
    try {
      await patchProfile({ remind_later: true })
    } finally {
      onClose()
    }
  }

  const renderField = (field: SupportedField) => {
    if (field === "date_of_birth") {
      return (
        <LocalizationProvider dateAdapter={AdapterMoment} key={field}>
          <DatePicker
            label={FIELD_LABELS[field]}
            value={dateValue}
            onChange={(val) => setDateValue(val)}
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" />
            )}
            maxDate={moment().subtract(1, "year")}
          />
        </LocalizationProvider>
      )
    }

    if (field === "department_id") {
      const options = departments.map((d: any) => ({
        label: d.name,
        value: d.id,
      }))

      return (
        <Autocomplete
          key={field}
          options={options}
          getOptionLabel={(opt) => opt.label}
          onChange={(_e, selected) =>
            handleFieldChange(field, selected?.value ?? null)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={FIELD_LABELS[field]}
              size="small"
              fullWidth
            />
          )}
        />
      )
    }

    return (
      <TextField
        key={field}
        label={FIELD_LABELS[field]}
        size="small"
        fullWidth
        value={formValues[field] ?? ""}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        multiline={field === "about_me"}
        rows={field === "about_me" ? 3 : 1}
      />
    )
  }

  return (
    <Dialog open={open} onClose={undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Actualiza tu información
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Tu organización solicita que mantengas tus datos actualizados.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {activeFields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No hay campos pendientes de actualización.
            </Typography>
          )}

          {activeFields.map((field) => renderField(field))}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="text"
          color="inherit"
          onClick={handleRemindLater}
          disabled={isSaving}
          sx={{ mr: "auto" }}
        >
          Recordarme después
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving || activeFields.length === 0}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {isSaving ? "Guardando..." : "Actualizar ahora"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Wrapper que maneja la lógica de visibilidad del modal.
 * Se debe colocar en el árbol de componentes que ya tiene acceso
 * al QueryClient (dentro de QueryClientProvider).
 */
export const ProfileUpdateModalController = () => {
  const [dismissed, setDismissed] = useState(false)
  const { data: requirements, isLoading } = useProfileUpdateRequirements()

  const shouldShow =
    !dismissed &&
    !isLoading &&
    !!requirements?.is_active &&
    !!requirements?.required_update

  if (!shouldShow) return null

  return (
    <ProfileUpdateModal
      open
      onClose={() => setDismissed(true)}
      requirements={requirements!}
    />
  )
}

export default ProfileUpdateModal
